import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCycleTime, formatDate, formatDuration } from 'lib/utils/date';
import _startCase from 'lodash/startCase';
import { formatValue } from 'lib/utils/text';
import { getRemainingOperationDuration } from 'pages/SelectJob/SelectWorkOrderOp/WorkOrderCard/helpers';
import moment from 'moment';
import useFetchJobSubmittedParts from 'lib/hooks/useFetchJobSubmittedParts';

import WorkOrderOpMachineName from 'components/WorkOrderOp/MachineName';
import {
  CardContainer,
  CardHead,
  CardHeader,
  CardBody,
  CardFoot,
  CardBodyRow,
  LargeText,
  MediumHeaderText,
  MediumText,
  TextContainer,
  DescriptionRow,
} from './styled';

export const DURATION_FORMAT = 'short';
const DURATION_OPTIONS = { trim: 'both' };

const WorkOrderOpCard = ({ workOrderOp }) => {
  const { t } = useTranslation();

  const {
    scheduledStartDate,
    workOrder,
    finishQuantity,
    setupTimeMs,
    cycleTimeMs,
    matchingPartOperations,
  } = workOrderOp || {};

  const expectedSetupTime = setupTimeMs / 1000;
  const expectedCycleTime = cycleTimeMs / 1000;

  const [requestJobSubmittedParts, { parts }] = useFetchJobSubmittedParts();

  useEffect(() => {
    if (workOrderOp) {
      requestJobSubmittedParts(workOrderOp);
    }
  }, [requestJobSubmittedParts, workOrderOp]);

  const orderProgressText = [parts?.totalParts, finishQuantity]
    .map((data) => {
      return formatValue(data)?.toLocaleString() || '-';
    })
    .join(' / ');

  if (!workOrderOp) {
    return null;
  }

  const remainingOperationDuration = getRemainingOperationDuration({
    operation: workOrderOp,
    currentOperation: workOrderOp,
  });

  const durationText =
    moment
      .duration(remainingOperationDuration)
      .format('h[h] m[m] s[s]', { largest: 2 }) || '0s';

  return (
    <CardContainer>
      <CardHead>
        <CardHeader>
          <CardBodyRow columns={2}>
            <TextContainer>
              <MediumHeaderText>{t('Expected setup')}</MediumHeaderText>
              <LargeText>
                {expectedSetupTime
                  ? formatDuration(
                      expectedSetupTime,
                      DURATION_FORMAT,
                      DURATION_OPTIONS
                    )
                  : _startCase(t('none'))}
              </LargeText>
            </TextContainer>

            <TextContainer>
              <MediumHeaderText>{t('Expected cycle time')}</MediumHeaderText>
              <LargeText>
                {formatCycleTime(expectedCycleTime, DURATION_OPTIONS)}
              </LargeText>
            </TextContainer>
          </CardBodyRow>
        </CardHeader>
      </CardHead>
      <CardBody>
        <CardBodyRow columns={2}>
          <TextContainer>
            <MediumHeaderText>{t('Order due date')}</MediumHeaderText>
            <MediumText>{formatDate(workOrder?.dueDate)}</MediumText>
          </TextContainer>

          <TextContainer>
            <MediumHeaderText>{t('Planned start date')}</MediumHeaderText>
            <MediumText>{formatDate(scheduledStartDate)}</MediumText>
          </TextContainer>
        </CardBodyRow>
        <CardBodyRow columns={2}>
          <TextContainer>
            <MediumHeaderText>{t('Estimated duration')}</MediumHeaderText>
            <MediumText>
              {cycleTimeMs ? durationText || 'N/A' : 'No cycle time'}
            </MediumText>
          </TextContainer>

          <TextContainer>
            <MediumHeaderText>{t('Resource')}</MediumHeaderText>
            <MediumText>
              <WorkOrderOpMachineName workOrderOp={workOrderOp} />
            </MediumText>
          </TextContainer>
        </CardBodyRow>

        {matchingPartOperations?.[0]?.description && (
          <DescriptionRow columns={1}>
            <MediumText>{matchingPartOperations?.[0]?.description}</MediumText>
          </DescriptionRow>
        )}
      </CardBody>
      <CardFoot>
        <LargeText>{orderProgressText}</LargeText>
      </CardFoot>
    </CardContainer>
  );
};

export default WorkOrderOpCard;
